import {FC, useState, useMemo} from "react";
import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Box,
	TextField,
	LinearProgress
} from "@mui/material";
import {DataGrid, GridColDef, GridSelectionModel} from "@mui/x-data-grid";
import {useGetCohorts} from "../hooks/useGetCohorts";
import {ExtendedAccessDto} from "@/app2/shared/data";

export interface IProps {
	currentSelected?: number[];
	onClose: (cohorts?: ExtendedAccessDto[]) => void;
}

interface CohortRow {
	id: number;
	title: string;
	publicLibraryType: string;
}

/**
 * Cohort Library Import Modal
 * 
 * Allows admin users to select cohorts to grant extended access to.
 * Uses DataGrid with server-side filtering and pagination.
 */
export const CohortLibraryImportModal: FC<IProps> = ({onClose, currentSelected}) => {
	const [selected, setSelected] = useState<GridSelectionModel>(currentSelected ?? []);
	const [searchTerm, setSearchTerm] = useState("");

	// Fetch all publicly visible cohorts
	const {data, isLoading} = useGetCohorts();

	// Client-side filtering and mapping
	const rows: CohortRow[] = useMemo(() => {
		if (!data?.data) return [];

		let filteredCohorts = data.data;

		// Client-side search filter
		if (searchTerm) {
			const lowerSearch = searchTerm.toLowerCase();
			filteredCohorts = data.data.filter(cohort => {
				const title = String(cohort.title || "").toLowerCase();
				const provider = String(cohort.publicLibraryType || "").toLowerCase();
				return title.includes(lowerSearch) || provider.includes(lowerSearch);
			});
		}

		return filteredCohorts.map(cohort => ({
			id: cohort.id!,
			title: String(cohort.title || `Cohort ${cohort.id}`),
			publicLibraryType: String(cohort.publicLibraryType || "-")
		}));
	}, [data, searchTerm]);

	const columns: GridColDef<CohortRow>[] = [
		{
			field: "title",
			headerName: "Library",
			flex: 1,
			minWidth: 200
		},
		{
			field: "publicLibraryType",
			headerName: "Provider",
			flex: 1,
			minWidth: 150
		}
	];

	const handleClose = () => onClose();

	const handleConfirm = () => {
		const selectedIds = new Set(selected as number[]);
		const selectedCohorts: ExtendedAccessDto[] = rows
			.filter(row => selectedIds.has(row.id))
			.map(row => ({id: row.id, title: row.title}));
		onClose(selectedCohorts);
	};

	const handleSelectionChange = (newSelection: GridSelectionModel) => {
		setSelected(newSelection);
	};

	return (
		<Dialog open={true} onClose={handleClose} maxWidth="md" fullWidth>
			<DialogTitle>Import Libraries</DialogTitle>
			<DialogContent sx={{minHeight: 500}}>
				<Box sx={{mb: 2, mt: 1}}>
					<TextField
						fullWidth
						placeholder="Search libraries..."
						value={searchTerm}
						onChange={(e) => setSearchTerm(e.target.value)}
						size="small"
					/>
				</Box>

				<Box sx={{height: 400, width: "100%"}}>
					{isLoading && <LinearProgress />}
					<DataGrid
						rows={rows}
						columns={columns}
						loading={isLoading}
						checkboxSelection
						keepNonExistentRowsSelected
						disableSelectionOnClick
						selectionModel={selected}
						onSelectionModelChange={handleSelectionChange}
						rowsPerPageOptions={[25, 50, 100, 200]}
						initialState={{
							pagination: {pageSize: 25}
						}}
						sx={{
							"& .MuiDataGrid-row": {cursor: "pointer"}
						}}
					/>
				</Box>
			</DialogContent>
			<DialogActions>
				<Button onClick={handleClose}>Cancel</Button>
				<Button variant="contained" onClick={handleConfirm}>
					{currentSelected?.length ? "Modify" : "Add"}
				</Button>
			</DialogActions>
		</Dialog>
	);
};

